/**
 * Iter 103.F · <ThreeMonthAttendancePanel />
 *
 * Sub-component of EssMyAttendance.jsx.  Owns its own axios fetch of
 *   GET /api/attendance/ess/three-month-summary
 * and re-fetches whenever the parent bumps `refreshKey` (only after a
 * successful check-in / -out / break / idle).  No global event bus.
 *
 * Design contract:
 *  • Formulas rendered in tooltips are STATIC strings inside this file,
 *    not a server-driven `formulas` block.
 *  • Every N/A field is rendered as an explicit "—" with a caption;
 *    never as "0".
 *  • Attendance Composition row is exactly 8 chips: Present · Half Day ·
 *    Leave · Absent · Weekly Off · Holiday · Late · Early.
 *  • Advisories drive amber notices at the top of the panel.
 *  • Loading / Error / Empty are handled inline; the rest of the page
 *    continues to render if this panel fails.
 */
import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

/**
 * Iter 103.F · Advisory-tag sanitization (owner-locked 2026-02-27).
 * Raw canonical advisory keys (e.g. `shift_master_not_approved_for_expected_hours`)
 * are internal / operational metadata. Regular employees see human-readable
 * copy only. The raw key surfaces ONLY for privileged roles that operate
 * the master data (super_admin / admin / hr_admin). No `?debug=1` back-door.
 */
const ADVISORY_PRIVILEGED_ROLES = new Set(["super_admin", "admin", "hr_admin"]);
const ADVISORY_FALLBACK_EMPLOYEE_COPY =
    "Additional advisory issued for this period — contact HR for details.";

function fmtMinutes(mins) {
    if (mins == null) return "—";
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
}

function fmtPct(v) {
    if (v == null) return "—";
    return `${Number(v).toFixed(1)}%`;
}

function fmtHoursOnly(mins) {
    if (mins == null) return "—";
    return `${(mins / 60).toFixed(2)}h`;
}

function fmtHours(h) {
    if (h == null) return "—";
    return `${Number(h).toFixed(2)}h`;
}

/**
 * Owner-locked 2026-02-27 · Decimal-hours → "Xh Ym" formatter for the
 * Shortfall/Excess hero. This is a pure formatter — the input is a
 * backend-provided scalar (already rounded server-side); the output is
 * a human-friendly string. NEVER used for KPI arithmetic.
 *  · 25.92h → "25h 55m"
 *  · 4.33h  → "4h 20m"
 *  · null   → "—"
 *  · rounding edge (m===60) rolls over cleanly.
 */
function fmtHoursMinutes(hoursDecimal) {
    if (hoursDecimal == null) return "—";
    const total = Math.max(0, Number(hoursDecimal));
    const h = Math.floor(total);
    const m = Math.round((total - h) * 60);
    if (m === 60) return `${h + 1}h 0m`;
    return `${h}h ${m}m`;
}

/**
 * Shortfall / Excess cell text. Rules (owner-locked 2026-02-27):
 *  · Renders "—" when the hours block is gated off (N/A).
 *  · If actual net >= adjusted required → "Excess +Xh" (positive framing).
 *  · If actual net <  adjusted required → "Shortfall Xh" (never negative).
 *  · If exactly on target → "On target".
 */
function fmtShortfallExcess(shortfallHours, excessHours) {
    if (shortfallHours == null && excessHours == null) return "—";
    const s = Number(shortfallHours || 0);
    const e = Number(excessHours || 0);
    if (e > 0) return `Excess +${e.toFixed(2)}h`;
    if (s > 0) return `Shortfall ${s.toFixed(2)}h`;
    return "On target";
}

/**
 * Compact monthly-card chip (owner-locked 2026-02-27).
 * Values are read directly from the backend HSE fields — the frontend
 * NEVER recomputes shortfall/excess/pct/achievement locally. Behaviour:
 *  · shortfall_hours == null && excess_hours == null → "Shortfall N/A"  (gated / unreliable authority)
 *  · excess_hours   > 0     → "Excess +Xh · Y%"  (Y = achievement_pct)
 *  · shortfall_hours > 0    → "Shortfall Xh · Y%"  (Y = achievement_pct)
 *  · both zero              → "On target"
 * Returns { label, tone } — tone is a Tailwind class string picked to
 * keep colour semantics consistent with the strip table row.
 */
function chipShortfallExcess(hours) {
    const s = hours.shortfall_hours;
    const e = hours.excess_hours;
    const ach = hours.achievement_pct;
    if (s == null && e == null) {
        return { label: "Shortfall N/A", tone: "bg-slate-50 text-slate-400 border-slate-200" };
    }
    const achTxt = ach == null ? "" : ` · ${Number(ach).toFixed(1)}%`;
    if (Number(e || 0) > 0) {
        return { label: `Excess +${Number(e).toFixed(2)}h${achTxt}`, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    }
    if (Number(s || 0) > 0) {
        return { label: `Shortfall ${Number(s).toFixed(2)}h${achTxt}`, tone: "bg-rose-50 text-rose-700 border-rose-200" };
    }
    return { label: "On target", tone: "bg-slate-50 text-slate-600 border-slate-200" };
}

const ADVISORY_COPY = {
    shift_not_assigned:
        "Shift not assigned for one or more eligible working days — expected-hours analysis unavailable for those days. Ask HR to assign a shift.",
    shift_master_not_approved_for_expected_hours:
        "Your assigned shift is not yet approved for expected-hours analytics — expected-hours numbers are unavailable until HR approves the shift.",
    holiday_calendar_not_assigned:
        "No holiday calendar linked to your record — holidays will render as N/A until HR links a calendar.",
    holiday_calendar_not_configured:
        "Your linked holiday calendar has no entries covering this period — holidays will render as N/A.",
    scheduled_working_days_not_reliable:
        "One or more source signals (shift or holiday calendar) is unavailable, so the working-days denominator is not fully reliable for this month.",
    idle_partially_tracked_any_month:
        "Idle tracking changed during the period — idle averages are computed only on tracked days.",
    snapshot_coverage_gap:
        "One or more eligible working days has no attendance snapshot — dashboard numbers and payroll numbers may legitimately diverge on those days.",
    mtd_current_month:
        "Current month is Month-To-Date — comparisons against closed months are advisory.",
    mid_month_joiner:
        "Employment began mid-month — pre-joining days are excluded from denominators.",
    mid_month_leaver:
        "Employment ended mid-month — post-leaving days are excluded from denominators.",
};

/**
 * KPI-reason strip copy (owner-approved 2026-02-27).  Explains exactly
 * why Standard Hours / Adjusted Required / Shortfall / Achievement %
 * render as "—".  Priority order (top = highest) matches the fix effort:
 *   1. shift_master_not_approved_for_expected_hours  (HR flag flip)
 *   2. shift_not_assigned                            (HR extends assignment)
 *   3. holiday_calendar_not_assigned                 (HR links calendar)
 *   4. holiday_calendar_not_configured               (HR adds entries)
 * Rendered only when a canonical advisory tag from the backend is
 * present — the UI NEVER invents a reason.
 */
const KPI_REASON_PRIORITY = [
    "shift_master_not_approved_for_expected_hours",
    "shift_not_assigned",
    "holiday_calendar_not_assigned",
    "holiday_calendar_not_configured",
];

const KPI_REASON_COPY = {
    shift_master_not_approved_for_expected_hours:
        "Standard Hours & Achievement are hidden because your shift is not yet approved for expected-hours analytics. Ask HR to approve the shift master.",
    shift_not_assigned:
        "Standard Hours & Achievement are hidden because a shift is not assigned on every working day of this period. Ask HR to assign a shift.",
    holiday_calendar_not_assigned:
        "Standard Hours & Achievement are hidden because no holiday calendar is linked to your record. Ask HR to link one.",
    holiday_calendar_not_configured:
        "Standard Hours & Achievement are hidden because your holiday calendar has no entries in this period. Ask HR to add the holidays.",
};

function pickKpiReason(advisories) {
    if (!advisories || advisories.length === 0) return null;
    const set = new Set(advisories);
    for (const tag of KPI_REASON_PRIORITY) {
        if (set.has(tag)) return tag;
    }
    return null;
}

/**
 * Owner-locked 2026-02-27 · Concise reason copy for the Shortfall hero
 * N/A state. Every string is derived from an existing backend advisory
 * tag — no client-side inference or classification. Priority order
 * mirrors `KPI_REASON_PRIORITY`; if no priority tag is present the
 * fallback surfaces the generic "Standard-hours source not available."
 */
const SHORT_REASON_COPY = {
    shift_master_not_approved_for_expected_hours:
        "Shift master not approved for expected-hours.",
    shift_not_assigned:
        "Shift not assigned on every eligible working day.",
    holiday_calendar_not_assigned:
        "Holiday calendar not linked to your record.",
    holiday_calendar_not_configured:
        "Holiday calendar has no entries in this period.",
};
const SHORT_REASON_FALLBACK = "Standard-hours source not available.";

const FORMULA_TIPS = {
    actual_attendance_days:
        "Actual Attendance = Present days + 0.5 × Half-Day days.",
    scheduled_working_days:
        "Scheduled Working Days = eligible days in your employment window that are neither weekly-off nor holiday, computed per-day using YOUR shift's weekly-off pattern.",
    achievement:
        "Achievement % = Actual Net Hours ÷ Adjusted Required Hours × 100. Adjusted Required = Σ per-day (shift_hours × (1 − approved leave equiv on that day)). Leave relief reduces the requirement exactly once; weekly-offs & holidays never enter the requirement.",
    variance:
        "Variance = Actual Net Minutes − Adjusted Expected Minutes.",
    headline_achievement:
        "3-Month Achievement % = Σ Actual Net Hours ÷ Σ Adjusted Required Hours × 100 (aggregate-of-underlying — never a mean of monthly percentages).",
    shortfall_excess:
        "Shortfall = max(0, Adjusted Required − Actual Net). Excess = max(0, Actual Net − Adjusted Required). Never negative; values above the standard render as Excess.",
    shortfall_pct:
        "Shortfall % = Shortfall Hours ÷ Adjusted Required Hours × 100. 0% when on-target or above standard.",
};

const COMPOSITION_ORDER = [
    { key: "present_days", label: "Present", cls: "bg-emerald-100 text-emerald-700" },
    { key: "half_days", label: "Half Day", cls: "bg-amber-100 text-amber-700" },
    { key: "leave_days_total_equiv", label: "Leave", cls: "bg-sky-100 text-sky-700" },
    { key: "absent_days", label: "Absent", cls: "bg-rose-100 text-rose-700" },
    {
        key: "pending_regularization_days",
        label: "Pending",
        cls: "bg-yellow-100 text-yellow-800",
        tip: "Days awaiting attendance regularization — neither paid nor LOP until resolved. If this count is non-zero while salary looks reduced, raise with HR to confirm the day was worked or correct the attendance source.",
    },
    { key: "weekly_offs", label: "Weekly Off", cls: "bg-slate-100 text-slate-600" },
    { key: "holidays", label: "Holiday", cls: "bg-indigo-100 text-indigo-700" },
    { key: "late_arrivals", label: "Late", cls: "bg-orange-100 text-orange-700" },
    { key: "early_departures", label: "Early", cls: "bg-orange-100 text-orange-700" },
];


export function ThreeMonthAttendancePanel({ refreshKey = 0 }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    const isAdvisoryPrivileged =
        !!user && ADVISORY_PRIVILEGED_ROLES.has(user.role);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data: resp } = await api.get(
                "/attendance/ess/three-month-summary",
            );
            setData(resp);
        } catch (err) {
            setError(formatApiError(err, "Failed to load 3-month attendance summary"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load, refreshKey]);

    if (loading) {
        return (
            <section
                className="mt-5 bg-white rounded-xl border border-slate-200 p-5 animate-pulse"
                data-testid="ess-3m-loading"
            >
                <div className="h-4 w-40 bg-slate-100 rounded mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="h-40 bg-slate-50 rounded" />
                    ))}
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section
                className="mt-5 bg-white rounded-xl border border-rose-200 p-5"
                data-testid="ess-3m-error"
            >
                <div className="flex items-center justify-between">
                    <div className="text-sm text-rose-700">{error}</div>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={load}
                        data-testid="ess-3m-retry"
                    >Retry</Button>
                </div>
            </section>
        );
    }

    if (!data || !data.months || data.months.length === 0) {
        return (
            <section
                className="mt-5 bg-white rounded-xl border border-slate-200 p-5"
                data-testid="ess-3m-empty"
            >
                <div className="text-sm text-slate-500">
                    No eligible attendance in the last three months.
                </div>
            </section>
        );
    }

    const shift = data.shift_context?.anchor_shift;
    const shiftSource = data.shift_context?.anchor_shift_source;
    const shiftLabel = shift
        ? `${shift.name || shift.code} · ${shift.full_day_hours}h/day`
        : "Shift not assigned";
    const shiftSourceLabel = !shift
        ? null
        : shiftSource === "default_shift"
            ? "via default shift"
            : shiftSource === "assignment"
                ? "via assigned shift"
                : null;
    const shiftApproved = shift && shift.approved_for_expected_hours;

    return (
        <section
            className="mt-5 space-y-4"
            data-testid="ess-3m-panel"
        >
            {/* Shift + idle-tracking ribbon */}
            <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                    <span
                        className={`text-xs px-2 py-1 rounded-full ${
                            shiftApproved
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-amber-50 text-amber-800 border border-amber-200"
                        }`}
                        data-testid="ess-3m-shift-source"
                    >
                        Standard: {shift ? `${shift.full_day_hours}h/day` : "N/A"} · {shiftLabel}
                        {shiftSourceLabel && ` · ${shiftSourceLabel}`}
                        {!shiftApproved && shift && " · not approved for expected-hours"}
                    </span>
                    <span
                        className="text-[11px] px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200"
                        data-testid="ess-3m-idle-mode"
                    >
                        Idle · {data.headline.idle_headline_mode.replace("_", " ")}
                    </span>
                </div>
                <span className="text-[11px] text-slate-400">
                    Anchor: {data.anchor_date} · timezone {data.timezone}
                </span>
            </div>

            {/* Advisories · raw keys visible only to super_admin / admin / hr_admin */}
            {data.advisories && data.advisories.length > 0 && (
                <div
                    className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1"
                    data-testid="ess-3m-advisories"
                    data-privileged={isAdvisoryPrivileged ? "1" : "0"}
                >
                    {data.advisories.map((tag) => {
                        const copy =
                            ADVISORY_COPY[tag] ||
                            (isAdvisoryPrivileged ? tag : ADVISORY_FALLBACK_EMPLOYEE_COPY);
                        return (
                            <div
                                key={tag}
                                className="text-xs text-amber-800 flex gap-2"
                                data-testid={`ess-3m-advisory-${tag}`}
                            >
                                {isAdvisoryPrivileged && (
                                    <span
                                        className="font-mono text-[10px] text-amber-600"
                                        data-testid={`ess-3m-advisory-tag-${tag}`}
                                    >
                                        {tag}
                                    </span>
                                )}
                                <span data-testid={`ess-3m-advisory-copy-${tag}`}>{copy}</span>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Row 2 — Three-month cards */}
            <div
                className="grid grid-cols-1 md:grid-cols-3 gap-3"
                data-testid="ess-3m-cards"
            >
                {data.months.map((m) => (
                    <MonthlyCard key={`${m.year}-${m.month}`} m={m} />
                ))}
            </div>

            {/* Row 3 — Hours vs Standard strip */}
            <HoursVsStandard months={data.months} headline={data.headline} advisories={data.advisories} />

            {/* Row 4 — Attendance Composition */}
            <CompositionStrip months={data.months} />
        </section>
    );
}


/**
 * Achievement-tone helper — the number itself comes verbatim from the
 * backend (`m.hours.achievement_pct`); we only pick a visual tone. This
 * is NOT a client-side KPI recomputation.
 */
function achievementTone(pct) {
    if (pct == null) return "text-slate-400";
    const n = Number(pct);
    if (n >= 100) return "text-emerald-700";
    if (n < 90) return "text-rose-700";
    return "text-slate-800";
}


function MonthlyCard({ m }) {
    const cardTestId = m.is_mtd
        ? "ess-3m-card-current"
        : `ess-3m-card-${m.year}-${String(m.month).padStart(2, "0")}`;
    const reliable = m.days.scheduled_working_days_reliable === true;
    const chip = chipShortfallExcess(m.hours);
    const shortfallPct = m.hours.shortfall_pct;
    const shortfallPctTone =
        shortfallPct == null
            ? "bg-slate-50 text-slate-400 border-slate-200"
            : Number(shortfallPct) > 0
                ? "bg-rose-50 text-rose-700 border-rose-200"
                : "bg-slate-50 text-slate-600 border-slate-200";
    return (
        <div
            className="rounded-xl border border-slate-200 bg-white p-5"
            data-testid={cardTestId}
        >
            {/* Header · label + MTD pill */}
            <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">{m.label}</div>
                {m.is_mtd && (
                    <Badge
                        className="bg-amber-100 text-amber-700"
                        data-testid="ess-3m-mtd-pill"
                    >
                        MTD · through {m.period_end_effective.slice(8, 10)} {m.label.split(" ")[0]}
                    </Badge>
                )}
            </div>

            {/* ── PRIMARY #1 · Actual Attendance (hero) ── */}
            <div className="mt-3 flex items-baseline gap-1" title={FORMULA_TIPS.actual_attendance_days}>
                <div
                    className="text-2xl font-semibold text-slate-800"
                    data-testid={`${cardTestId}-actual`}
                >
                    {m.days.actual_attendance_days}
                </div>
                {reliable ? (
                    <div
                        className="text-sm text-slate-500"
                        data-testid={`${cardTestId}-denominator`}
                    >
                        / {m.days.scheduled_working_days} working days
                    </div>
                ) : (
                    <div
                        className="text-sm text-slate-500"
                        data-testid={`${cardTestId}-denominator-na`}
                        title="Working-days denominator is not authoritative — shift or holiday calendar is missing/unapproved."
                    >
                        <span aria-label="not available">/ —</span>{" "}
                        <span className="text-[11px] text-amber-700">
                            working days · N/A (provisional)
                        </span>
                    </div>
                )}
            </div>

            {/* ── PRIMARY #2 · Achievement % (prominent) ── */}
            <div
                className="mt-3"
                data-testid={`${cardTestId}-achievement-primary`}
                title={FORMULA_TIPS.achievement}
            >
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Achievement</div>
                <div
                    className={`text-xl font-semibold ${achievementTone(m.hours.achievement_pct)}`}
                    data-testid={`${cardTestId}-achievement-primary-value`}
                >
                    {fmtPct(m.hours.achievement_pct)}
                </div>
            </div>

            {/* ── PRIMARY #3 · Adjusted Required · Actual Net ── */}
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div
                    className="rounded-md border border-slate-100 bg-slate-50 p-2"
                    data-testid={`${cardTestId}-adjusted-required`}
                    title={FORMULA_TIPS.achievement}
                >
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                        Adjusted Required
                    </div>
                    <div
                        className="text-sm font-semibold text-slate-800 font-mono"
                        data-testid={`${cardTestId}-adjusted-required-value`}
                    >
                        {fmtHours(m.hours.adjusted_required_hours)}
                    </div>
                </div>
                <div
                    className="rounded-md border border-slate-100 bg-slate-50 p-2"
                    data-testid={`${cardTestId}-actual-net`}
                >
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                        Actual Net
                    </div>
                    <div
                        className="text-sm font-semibold text-slate-800 font-mono"
                        data-testid={`${cardTestId}-actual-net-value`}
                    >
                        {fmtHours(m.hours.actual_net_hours)}
                    </div>
                </div>
            </div>

            {/* ── PRIMARY #4 · Shortfall/Excess Hours + Shortfall % ── */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
                <span
                    className={`inline-block text-[11px] px-2 py-1 rounded-full border ${chip.tone}`}
                    data-testid={`${cardTestId}-hse-chip`}
                    title={FORMULA_TIPS.shortfall_excess}
                >
                    {chip.label}
                </span>
                {shortfallPct != null && (
                    <span
                        className={`inline-block text-[11px] px-2 py-1 rounded-full border ${shortfallPctTone}`}
                        data-testid={`${cardTestId}-shortfall-pct-chip`}
                        title={FORMULA_TIPS.shortfall_pct}
                    >
                        Shortfall {fmtPct(shortfallPct)}
                    </span>
                )}
            </div>

            {/* ── SECONDARY · demoted counts + averages ── */}
            <div className="mt-4 pt-3 border-t border-slate-100">
                <div
                    className="text-[11px] text-slate-500"
                    data-testid={`${cardTestId}-secondary-days`}
                >
                    L {m.days.leave_days_total_equiv.toFixed(1)}
                    {" · "}H {m.days.holidays == null ? "—" : m.days.holidays}
                    {" · "}WO {m.days.weekly_offs}
                    {" · "}Late {m.days.late_arrivals}
                    {" · "}Early {m.days.early_departures}
                    {m.days.missing_snapshot_days > 0 && (
                        <> · <span className="text-amber-700">Missing {m.days.missing_snapshot_days}</span></>
                    )}
                </div>
                <div
                    className="mt-1 text-[11px] text-slate-500"
                    data-testid={`${cardTestId}-secondary-averages`}
                >
                    Avg Break {fmtMinutes(m.hours.avg_break_per_actual_day)}
                    {" · "}Avg Idle {
                        m.idle.mode === "not_tracked"
                            ? "N/A · not tracked"
                            : fmtMinutes(m.idle.avg_idle_per_tracked_day)
                    }
                </div>
            </div>
        </div>
    );
}


/**
 * Shortfall / Excess HERO summary (owner-locked 2026-02-27).
 *
 * Renders one of four owner-mandated states directly above the Working
 * Hours vs Standard table:
 *
 *   · SHORTFALL   → rose card · "SHORTFALL · Xh Ym · Z% below required ·
 *                    Achievement A%"
 *   · EXCESS      → emerald card · "EXCESS · +Xh Ym · Achievement A%"
 *   · ON TARGET   → neutral card · "ON TARGET · Achievement 100.0%"
 *   · N/A (gated) → slate card · "SHORTFALL · N/A · Expected-hours setup
 *                    incomplete · <concise reason from advisory>"
 *
 * Every displayed number is a verbatim backend field (`headline.kpis.*`).
 * The frontend NEVER recomputes shortfall / excess / achievement / %.
 * `fmtHoursMinutes` is a pure format-only helper — not KPI arithmetic.
 */
function ShortfallHero({ headline, advisories }) {
    const s = headline.kpis.shortfall_hours;
    const e = headline.kpis.excess_hours;
    const pct = headline.kpis.shortfall_pct;
    const ach = headline.kpis.achievement_pct;

    // ── State A · gated N/A (backend cannot compute expected hours) ──
    if (s == null && e == null) {
        const tag = pickKpiReason(advisories);
        const reason = (tag && SHORT_REASON_COPY[tag]) || SHORT_REASON_FALLBACK;
        return (
            <div
                className="mb-4 rounded-xl border border-slate-300 bg-slate-50 p-4"
                data-testid="ess-3m-shortfall-hero"
                data-state="na"
            >
                <div
                    className="text-[11px] uppercase tracking-widest font-semibold text-slate-500"
                    data-testid="ess-3m-hero-label"
                >
                    Shortfall
                </div>
                <div
                    className="mt-1 text-3xl font-bold text-slate-400"
                    data-testid="ess-3m-hero-value"
                >
                    N/A
                </div>
                <div
                    className="mt-2 text-sm font-medium text-slate-700"
                    data-testid="ess-3m-hero-caption"
                >
                    Expected-hours setup incomplete
                </div>
                <div
                    className="mt-0.5 text-xs text-slate-600"
                    data-testid="ess-3m-hero-reason"
                    data-tag={tag || "fallback"}
                >
                    {reason}
                </div>
            </div>
        );
    }

    // ── State B · Excess (net > adjusted) ──
    if (Number(e || 0) > 0) {
        return (
            <div
                className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4"
                data-testid="ess-3m-shortfall-hero"
                data-state="excess"
            >
                <div
                    className="text-[11px] uppercase tracking-widest font-semibold text-emerald-700"
                    data-testid="ess-3m-hero-label"
                >
                    Excess
                </div>
                <div
                    className="mt-1 text-3xl font-bold text-emerald-800"
                    data-testid="ess-3m-hero-value"
                >
                    +{fmtHoursMinutes(e)}
                </div>
                <div
                    className="mt-2 text-sm font-medium text-emerald-700"
                    data-testid="ess-3m-hero-achievement"
                >
                    Achievement {fmtPct(ach)}
                </div>
            </div>
        );
    }

    // ── State C · Shortfall (net < adjusted) ──
    if (Number(s || 0) > 0) {
        return (
            <div
                className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4"
                data-testid="ess-3m-shortfall-hero"
                data-state="shortfall"
            >
                <div
                    className="text-[11px] uppercase tracking-widest font-semibold text-rose-700"
                    data-testid="ess-3m-hero-label"
                >
                    Shortfall
                </div>
                <div
                    className="mt-1 text-3xl font-bold text-rose-800"
                    data-testid="ess-3m-hero-value"
                >
                    {fmtHoursMinutes(s)}
                </div>
                <div
                    className="mt-2 text-sm font-medium text-rose-700"
                    data-testid="ess-3m-hero-below"
                >
                    {fmtPct(pct)} below required
                </div>
                <div
                    className="mt-0.5 text-sm font-medium text-rose-700"
                    data-testid="ess-3m-hero-achievement"
                >
                    Achievement {fmtPct(ach)}
                </div>
            </div>
        );
    }

    // ── State D · On target (both zero · adjusted == net) ──
    return (
        <div
            className="mb-4 rounded-xl border border-slate-200 bg-white p-4"
            data-testid="ess-3m-shortfall-hero"
            data-state="on_target"
        >
            <div
                className="text-[11px] uppercase tracking-widest font-semibold text-slate-700"
                data-testid="ess-3m-hero-label"
            >
                On Target
            </div>
            <div
                className="mt-2 text-sm font-medium text-slate-700"
                data-testid="ess-3m-hero-achievement"
            >
                Achievement {fmtPct(ach)}
            </div>
        </div>
    );
}


function HoursVsStandard({ months, headline, advisories }) {
    const reasonTag = pickKpiReason(advisories);
    return (
        <div
            className="rounded-xl border border-slate-200 bg-white p-5"
            data-testid="ess-3m-hours-strip"
        >
            <div className="text-sm font-semibold text-slate-800 mb-3">Working Hours vs Standard</div>
            {/* Shortfall / Excess HERO (owner-locked 2026-02-27) · values verbatim
                from backend headline.kpis · zero client-side KPI arithmetic. */}
            <ShortfallHero headline={headline} advisories={advisories} />
            {reasonTag && (
                <div
                    className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3"
                    data-testid="ess-3m-kpi-reason"
                >
                    <div className="text-xs font-semibold text-amber-900 mb-0.5">Why some numbers show &ldquo;—&rdquo;</div>
                    <div
                        className="text-xs text-amber-800"
                        data-testid={`ess-3m-kpi-reason-${reasonTag}`}
                    >
                        {KPI_REASON_COPY[reasonTag]}
                    </div>
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500">
                        <tr>
                            <th className="text-left font-medium py-1"></th>
                            {months.map((m) => (
                                <th key={`h-${m.year}-${m.month}`} className="text-right font-medium py-1 pl-4">
                                    {m.label}
                                </th>
                            ))}
                            <th className="text-right font-medium py-1 pl-4">3-Mo</th>
                        </tr>
                    </thead>
                    <tbody className="text-slate-700">
                        <tr className="border-t border-slate-100" data-testid="ess-3m-row-standard-hours">
                            <td className="py-1.5 text-xs text-slate-500">Standard Hours</td>
                            {months.map((m) => {
                                const suffix = `${m.year}-${String(m.month).padStart(2, "0")}`;
                                return (
                                    <td
                                        key={`ne-${suffix}`}
                                        className="text-right py-1.5 pl-4 font-mono text-xs"
                                        data-testid={`ess-3m-standard-hours-${suffix}`}
                                    >
                                        {fmtHours(m.hours.nominal_standard_hours)}
                                    </td>
                                );
                            })}
                            <td
                                className="text-right py-1.5 pl-4 font-mono text-xs"
                                data-testid="ess-3m-headline-standard-hours"
                            >
                                {fmtHours(headline.totals.nominal_standard_hours)}
                            </td>
                        </tr>
                        <tr className="border-t border-slate-100" data-testid="ess-3m-row-adjusted-required">
                            <td className="py-1.5 text-xs text-slate-500">Adjusted Required</td>
                            {months.map((m) => {
                                const suffix = `${m.year}-${String(m.month).padStart(2, "0")}`;
                                return (
                                    <td
                                        key={`ae-${suffix}`}
                                        className="text-right py-1.5 pl-4 font-mono text-xs"
                                        data-testid={`ess-3m-adjusted-required-${suffix}`}
                                    >
                                        {fmtHours(m.hours.adjusted_required_hours)}
                                    </td>
                                );
                            })}
                            <td
                                className="text-right py-1.5 pl-4 font-mono text-xs"
                                data-testid="ess-3m-headline-adjusted-required"
                            >
                                {fmtHours(headline.totals.adjusted_required_hours)}
                            </td>
                        </tr>
                        <tr className="border-t border-slate-100" data-testid="ess-3m-row-actual-net">
                            <td className="py-1.5 text-xs text-slate-500">Actual Net</td>
                            {months.map((m) => {
                                const suffix = `${m.year}-${String(m.month).padStart(2, "0")}`;
                                return (
                                    <td
                                        key={`an-${suffix}`}
                                        className="text-right py-1.5 pl-4 font-mono text-xs"
                                        data-testid={`ess-3m-actual-net-${suffix}`}
                                    >
                                        {fmtHours(m.hours.actual_net_hours)}
                                    </td>
                                );
                            })}
                            <td
                                className="text-right py-1.5 pl-4 font-mono text-xs"
                                data-testid="ess-3m-headline-actual-net"
                            >
                                {fmtHours(headline.totals.actual_net_hours)}
                            </td>
                        </tr>
                        <tr className="border-t border-slate-100" data-testid="ess-3m-row-shortfall-excess">
                            <td
                                className="py-1.5 text-xs text-slate-500"
                                title={FORMULA_TIPS.shortfall_excess}
                            >
                                Shortfall / Excess
                            </td>
                            {months.map((m) => {
                                const suffix = `${m.year}-${String(m.month).padStart(2, "0")}`;
                                const cell = fmtShortfallExcess(m.hours.shortfall_hours, m.hours.excess_hours);
                                const tone = cell.startsWith("Excess")
                                    ? "text-emerald-700"
                                    : cell.startsWith("Shortfall")
                                        ? "text-rose-700"
                                        : cell === "—"
                                            ? "text-slate-400"
                                            : "text-slate-700";
                                return (
                                    <td
                                        key={`se-${suffix}`}
                                        className={`text-right py-1.5 pl-4 font-mono text-xs ${tone}`}
                                        data-testid={`ess-3m-shortfall-excess-${suffix}`}
                                    >
                                        {cell}
                                    </td>
                                );
                            })}
                            <td
                                className={`text-right py-1.5 pl-4 font-mono text-xs font-semibold ${
                                    fmtShortfallExcess(headline.kpis.shortfall_hours, headline.kpis.excess_hours).startsWith("Excess")
                                        ? "text-emerald-700"
                                        : fmtShortfallExcess(headline.kpis.shortfall_hours, headline.kpis.excess_hours).startsWith("Shortfall")
                                            ? "text-rose-700"
                                            : "text-slate-700"
                                }`}
                                data-testid="ess-3m-headline-shortfall-excess"
                                title={FORMULA_TIPS.shortfall_excess}
                            >
                                {fmtShortfallExcess(headline.kpis.shortfall_hours, headline.kpis.excess_hours)}
                            </td>
                        </tr>
                        <tr className="border-t border-slate-100" data-testid="ess-3m-row-shortfall-pct">
                            <td
                                className="py-1.5 text-xs text-slate-500"
                                title={FORMULA_TIPS.shortfall_pct}
                            >
                                Shortfall %
                            </td>
                            {months.map((m) => {
                                const suffix = `${m.year}-${String(m.month).padStart(2, "0")}`;
                                return (
                                    <td
                                        key={`sp-${suffix}`}
                                        className="text-right py-1.5 pl-4 font-mono text-xs"
                                        data-testid={`ess-3m-shortfall-pct-${suffix}`}
                                    >
                                        {fmtPct(m.hours.shortfall_pct)}
                                    </td>
                                );
                            })}
                            <td
                                className="text-right py-1.5 pl-4 font-mono text-xs font-semibold"
                                data-testid="ess-3m-headline-shortfall-pct"
                                title={FORMULA_TIPS.shortfall_pct}
                            >
                                {fmtPct(headline.kpis.shortfall_pct)}
                            </td>
                        </tr>
                        <tr className="border-t border-slate-100" data-testid="ess-3m-row-achievement">
                            <td className="py-1.5 text-xs text-slate-500" title={FORMULA_TIPS.achievement}>Achievement %</td>
                            {months.map((m) => (
                                <td
                                    key={`ach-${m.year}-${m.month}`}
                                    className="text-right py-1.5 pl-4 font-mono text-xs"
                                    data-testid={`ess-3m-achievement-${m.year}-${String(m.month).padStart(2, "0")}`}
                                >
                                    {fmtPct(m.hours.achievement_pct)}
                                </td>
                            ))}
                            <td
                                className="text-right py-1.5 pl-4 font-mono text-xs font-semibold"
                                data-testid="ess-3m-headline-achievement"
                                title={FORMULA_TIPS.headline_achievement}
                            >
                                {fmtPct(headline.kpis.achievement_pct)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="mt-3 text-[11px] text-slate-500" data-testid="ess-3m-headline">
                3-Month Headline · Attendance {fmtPct(headline.kpis.actual_attendance_pct)}
                {" · "}Avg Net {fmtMinutes(headline.kpis.avg_net_per_actual_day)}/day
                {" · "}Achievement {fmtPct(headline.kpis.achievement_pct)}
            </div>
        </div>
    );
}


function CompositionStrip({ months }) {
    return (
        <div
            className="rounded-xl border border-slate-200 bg-white p-5"
            data-testid="ess-3m-composition-strip"
        >
            <div className="text-sm font-semibold text-slate-800 mb-3">Attendance Composition</div>
            <div className="space-y-2">
                {months.map((m) => {
                    const key = `${m.year}-${String(m.month).padStart(2, "0")}`;
                    return (
                        <div
                            key={key}
                            className="flex items-center gap-2 flex-wrap"
                            data-testid={`ess-3m-comp-row-${key}`}
                        >
                            <span className="text-xs font-semibold text-slate-600 w-20">{m.label.split(" ")[0]}</span>
                            {COMPOSITION_ORDER.map((c) => {
                                let value = m.days[c.key];
                                if (value == null) {
                                    value = "—";
                                } else if (typeof value === "number") {
                                    value = c.key === "leave_days_total_equiv"
                                        ? value.toFixed(1)
                                        : value;
                                }
                                return (
                                    <span
                                        key={c.key}
                                        className={`text-[11px] px-2 py-0.5 rounded ${c.cls}`}
                                        title={c.tip || undefined}
                                        data-testid={`ess-3m-comp-${key}-${c.key}`}
                                    >
                                        {c.label} {value}
                                    </span>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


function Stat({ label, value, tip }) {
    return (
        <div className="rounded-md border border-slate-100 bg-slate-50 p-2" title={tip || undefined}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="text-sm font-semibold text-slate-800 truncate">{value}</div>
        </div>
    );
}

export default ThreeMonthAttendancePanel;
